import { useEffect, useRef, useState } from "react"
import { terminalApi } from "@/lib/terminalApi"

/**
 * Leerlauf: nach `sekunden` ohne Berührung/Taste wird abgemeldet. Bei Aktivität
 * wird die Sitzung am Server höchstens alle 30 s verlängert.
 */
export function useLeerlauf(sekunden: number, onAblauf: () => void, aktiv: boolean) {
  const [rest, setRest] = useState(sekunden)
  const letzte = useRef(Date.now())
  const letzterPing = useRef(Date.now())
  const ablauf = useRef(onAblauf)
  ablauf.current = onAblauf

  useEffect(() => {
    if (!aktiv) return
    letzte.current = Date.now()
    const merken = () => {
      letzte.current = Date.now()
      if (Date.now() - letzterPing.current > 30_000) {
        letzterPing.current = Date.now()
        void terminalApi.post("/sitzung").catch(() => undefined)
      }
    }
    window.addEventListener("pointerdown", merken, true)
    window.addEventListener("keydown", merken, true)
    const takt = window.setInterval(() => {
      const r = Math.max(0, sekunden - Math.floor((Date.now() - letzte.current) / 1000))
      setRest(r)
      if (r === 0) ablauf.current()
    }, 500)
    return () => {
      window.removeEventListener("pointerdown", merken, true)
      window.removeEventListener("keydown", merken, true)
      window.clearInterval(takt)
    }
  }, [sekunden, aktiv])
  return rest
}

/**
 * Erkennt einen RFID-Leser mit Tastatur-Emulation: viele Zeichen in sehr kurzer Zeit,
 * abgeschlossen mit Enter. Gibt die gelesene Nummer an `onChip` weiter.
 * Menschen tippen deutlich langsamer als 40 ms je Zeichen.
 */
export function useChipLeser(onChip: (nummer: string) => void, aktiv: boolean) {
  const puffer = useRef<{ zeichen: string; zeit: number }[]>([])
  const callback = useRef(onChip)
  callback.current = onChip
  useEffect(() => {
    if (!aktiv) return
    const taste = (e: KeyboardEvent) => {
      const jetzt = performance.now()
      const p = puffer.current
      if (p.length && jetzt - p[p.length - 1].zeit > 60) p.length = 0
      if (e.key === "Enter") {
        const nummer = p.map((x) => x.zeichen).join("")
        const schnell = p.length >= 4 && p[p.length - 1].zeit - p[0].zeit < p.length * 40
        p.length = 0
        if (schnell) {
          // stopImmediatePropagation: andere Enter-Handler am selben window (Feldwechsel) nicht auslösen
          e.preventDefault()
          e.stopImmediatePropagation()
          callback.current(nummer)
        }
        return
      }
      if (e.key.length === 1) p.push({ zeichen: e.key, zeit: jetzt })
    }
    window.addEventListener("keydown", taste, true)
    return () => window.removeEventListener("keydown", taste, true)
  }, [aktiv])
}

/** Uhr für den Sperrbildschirm. */
export function useUhr(): Date {
  const [jetzt, setJetzt] = useState(new Date())
  useEffect(() => {
    const t = window.setInterval(() => setJetzt(new Date()), 1000)
    return () => window.clearInterval(t)
  }, [])
  return jetzt
}
